import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import Sidebar from '../../../components/ui/Sidebar';
import Header from '../../../components/ui/Header';
import Icon from '../../../components/AppIcon';
import PageHeader from '../../../components/ui/PageHeader';
import WorkforceTable from './components/WorkforceTable';
import AddPersonnelModal from './components/AddPersonnelModal';
import CredentialsModal from './components/CredentialsModal';
import AccessDenied from '../../../components/ui/AccessDenied';
import PermissionManagementModal from '../../../components/permissions/PermissionManagementModal';
import ChangeRoleModal from './components/ChangeRoleModal';
import { workforceService } from '../../../services/workforceService';

const WorkforceManagement = () => {
  useAuth(); // For auth context
  const { currentCompany } = useCompany();
  const { hasPermission, loading: permLoading, userRole } = usePermissions();
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalType, setModalType] = useState('employee');
  const [credentialsModalData, setCredentialsModalData] = useState(null);
  const [permissionModalMember, setPermissionModalMember] = useState(null);
  const [roleModalMember, setRoleModalMember] = useState(null);

  // Load workforce data when company changes
  useEffect(() => {
    if (currentCompany?.id) {
      loadWorkforceData();
    }
  }, [currentCompany?.id]);

  // Set default active tab based on permissions
  useEffect(() => {
    if (!permLoading) {
      // If user only has one view permission, set that as the active tab
      if (hasPermission('view_contractors') && !hasPermission('view_employees')) {
        setActiveTab('contractors');
      } else if (hasPermission('view_employees') && !hasPermission('view_contractors')) {
        setActiveTab('employees');
      }
    }
  }, [permLoading, hasPermission]);

  const loadWorkforceData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyId = currentCompany?.id;
      if (!companyId) {
        setError('No company selected');
        return;
      }

      // Load employees and contractors
      const [employeesData, contractorsData] = await Promise.all([
        workforceService?.getCompanyEmployees(companyId),
        workforceService?.getCompanyContractors(companyId)
      ]);

      setEmployees(employeesData || []);
      setContractors(contractorsData || []);
    } catch (err) {
      console.error('Error loading workforce:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (memberId, newStatus) => {
    try {
      await workforceService?.updateMemberStatus(memberId, newStatus, currentCompany?.id);
      // Reload data to reflect changes
      await loadWorkforceData();
    } catch (err) {
      console.error('Error updating member status:', err);
      setError(err?.message);
    }
  };

  const handleAddPersonnel = (type) => {
    setModalType(type);
    setIsAddModalOpen(true);
  };

  const handlePersonnelCreated = (result) => {
    // If new user was created, show credentials modal
    if (result.type === 'created' && result.data?.credentials) {
      setCredentialsModalData({
        email: result.data.credentials.email,
        password: result.data.credentials.password,
        masterId: result.data.master_id,
        role: result.data.role
      });
    }
    // Reload the workforce data
    loadWorkforceData();
  };

  const handleCloseCredentials = () => {
    setCredentialsModalData(null);
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await workforceService?.updateMemberRole(memberId, newRole, currentCompany?.id);
      // Reload data to reflect changes
      await loadWorkforceData();
    } catch (err) {
      console.error('Error changing role:', err);
      throw err; // Re-throw so modal can display error
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />

      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 lg:ml-60 mt-16 p-8">
        <PageHeader
          title="Workforce Management"
          subtitle="Manage your team members and contractors"
          description=""
          actions={null}
        />

        {/* Permission Loading State */}
        {permLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        )}

        {/* Access Check - Show denied if no workforce view permissions */}
        {!permLoading && !hasPermission('view_employees') && !hasPermission('view_contractors') && (
          <AccessDenied
            permission="view workforce"
            customMessage="You don't have permission to view employees or contractors."
          />
        )}

        {/* Content only shown if user has at least one workforce view permission */}
        {!permLoading && (hasPermission('view_employees') || hasPermission('view_contractors')) && (
          <>
            {/* Error Display */}
            {error && (
              <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={20} color="#ef4444" />
                  <p className="text-red-400">{error}</p>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {hasPermission('view_employees') && (
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={24} color="#3b82f6" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-1">{employees?.length}</h3>
                  <p className="text-text-secondary text-sm">Total Employees</p>
                </div>
              )}

              {hasPermission('view_contractors') && (
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-orange-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Icon name="UserCheck" size={24} color="#FF7A00" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-1">{contractors?.length}</h3>
                  <p className="text-text-secondary text-sm">Total Contractors</p>
                </div>
              )}

              {(hasPermission('view_employees') || hasPermission('view_contractors')) && (
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-green-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle" size={24} color="#00C47D" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-text-primary mb-1">
                    {[
                      ...(hasPermission('view_employees') ? employees : []),
                      ...(hasPermission('view_contractors') ? contractors : [])
                    ]?.filter(m => m?.isActive)?.length}
                  </h3>
                  <p className="text-text-secondary text-sm">Active Members</p>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="mt-8 bg-surface border border-background rounded-xl overflow-hidden">
              <div className="flex border-b border-border">
                {hasPermission('view_employees') && (
                  <button
                    onClick={() => setActiveTab('employees')}
                    className={`flex-1 px-6 py-4 font-medium transition-colors ${activeTab === 'employees' ? 'bg-background text-text-primary border-b-2 border-orange-500' : 'text-text-secondary hover:text-text-primary'
                      }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="Users" size={20} />
                      <span>Employees</span>
                      <span className="ml-2 px-2 py-1 bg-background rounded-full text-xs">
                        {employees?.length}
                      </span>
                    </div>
                  </button>
                )}
                {hasPermission('view_contractors') && (
                  <button
                    onClick={() => setActiveTab('contractors')}
                    className={`flex-1 px-6 py-4 font-medium transition-colors ${activeTab === 'contractors' ? 'bg-background text-text-primary border-b-2 border-orange-500' : 'text-text-secondary hover:text-text-primary'
                      }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="UserCheck" size={20} />
                      <span>Contractors</span>
                      <span className="ml-2 px-2 py-1 bg-background rounded-full text-xs">
                        {contractors?.length}
                      </span>
                    </div>
                  </button>
                )}
              </div>

              {/* Add Button - only show if user can manage the current tab type */}
              {((activeTab === 'employees' && hasPermission('manage_employees')) ||
                (activeTab === 'contractors' && hasPermission('manage_contractors'))) && (
                <div className="p-6 border-b border-border">
                  <button
                    onClick={() => handleAddPersonnel(activeTab === 'employees' ? 'employee' : 'contractor')}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-white font-medium hover:from-orange-600 hover:to-orange-700 transition-colors shadow-lg shadow-orange-500/20"
                  >
                    <Icon name="UserPlus" size={20} />
                    <span>Add {activeTab === 'employees' ? 'Employee' : 'Contractor'}</span>
                  </button>
                </div>
              )}

              {/* Table Content */}
              <div className="p-6">
                {activeTab === 'employees' ? (
                  hasPermission('view_employees') ? (
                    <WorkforceTable
                      members={employees}
                      onStatusChange={handleStatusChange}
                      onRoleChange={(member) => setRoleModalMember(member)}
                      loading={loading}
                      type="employees"
                      canManage={hasPermission('manage_employees')}
                      canManagePermissions={hasPermission('manage_permissions')}
                      onManagePermissions={(member) => setPermissionModalMember(member)}
                    />
                  ) : (
                    <AccessDenied permission="view employees" />
                  )
                ) : (
                  hasPermission('view_contractors') ? (
                    <WorkforceTable
                      members={contractors}
                      onStatusChange={handleStatusChange}
                      onRoleChange={(member) => setRoleModalMember(member)}
                      loading={loading}
                      type="contractors"
                      canManage={hasPermission('manage_contractors')}
                      canManagePermissions={hasPermission('manage_permissions')}
                      onManagePermissions={(member) => setPermissionModalMember(member)}
                    />
                  ) : (
                    <AccessDenied permission="view contractors" />
                  )
                )}
              </div>
            </div>
          </>
        )}
      </main>

      {/* Add Personnel Modal */}
      <AddPersonnelModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        type={modalType}
        onSuccess={handlePersonnelCreated}
      />

      {/* Credentials Modal - shown after creating a new user */}
      <CredentialsModal
        isOpen={!!credentialsModalData}
        onClose={handleCloseCredentials}
        credentials={credentialsModalData}
        memberType={modalType}
      />

      {/* Permission Management Modal */}
      <PermissionManagementModal
        isOpen={!!permissionModalMember}
        onClose={() => setPermissionModalMember(null)}
        member={permissionModalMember}
        onUpdate={loadWorkforceData}
      />

      {/* Change Role Modal */}
      <ChangeRoleModal
        isOpen={!!roleModalMember}
        onClose={() => setRoleModalMember(null)}
        member={roleModalMember}
        onSave={handleRoleChange}
        currentUserRole={userRole}
      />
    </div>
  );
};

export default WorkforceManagement;